// Stats tab for ItemReportModal (ITEM_REPORT_ENHANCEMENTS_PLAN.md D-IE-1..3,
// Task 4.1). Presentation only — every number arrives pre-computed by
// buildOccurrenceStats/buildDoseTotals/buildSymptomStats (Phase 1/2).

import type { ReactNode } from "react";
import type { OccurrenceStats, SymptomStatRow, TimeBucket } from "@medassist/shared";
import { TIME_BUCKETS } from "@medassist/shared";

// Duplicated in miniature from SymptomStatsView.tsx (not exported there;
// ITEM_REPORT_ENHANCEMENTS_PLAN.md Task 4.1 says duplicate rather than refactor).
function formatResolutionMs(ms: number): string {
  const days = ms / 86_400_000;
  return days < 1 ? `${Math.round((ms / 3_600_000) * 10) / 10}h` : `${Math.round(days * 10) / 10}d`;
}

function formatDate(date: string): string {
  return new Date(date + "T12:00:00Z").toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    timeZone: "UTC",
  });
}

function formatHour(hour: number): string {
  const period = hour < 12 ? "AM" : "PM";
  let h12 = hour % 12;
  if (h12 === 0) h12 = 12;
  return `${h12} ${period}`;
}

const BUCKET_LABEL: Record<TimeBucket, string> = {
  morning: "Morning",
  afternoon: "Afternoon",
  evening: "Evening",
  night: "Night",
};

const DOW_LABEL = ["S", "M", "T", "W", "T", "F", "S"];

function Card({ title, hint, children }: { title: string; hint?: string; children: ReactNode }) {
  return (
    <div className="rounded-lg border border-gray-200 dark:border-gray-800 p-3">
      <h3 className="mb-0.5 text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
        {title}
      </h3>
      {hint && <p className="mb-1.5 text-[11px] text-gray-400 dark:text-gray-600">{hint}</p>}
      <dl className="space-y-1">{children}</dl>
    </div>
  );
}

function Stat({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <div className="flex items-baseline justify-between gap-2">
      <dt className="text-xs text-gray-500 dark:text-gray-400">
        {label}
        {hint && <span className="ml-1 text-[10px] text-gray-400 dark:text-gray-600">({hint})</span>}
      </dt>
      <dd className="text-sm font-semibold text-gray-900 dark:text-gray-100">{value}</dd>
    </div>
  );
}

function Delta({ current, prev }: { current: number; prev: number }) {
  if (prev === 0) return <span className="text-gray-500 dark:text-gray-400">—</span>;
  const pct = ((current - prev) / Math.abs(prev)) * 100;
  const dir = Math.abs(pct) < 0.5 ? "flat" : pct > 0 ? "up" : "down";
  return (
    <span className="text-gray-500 dark:text-gray-400">
      {dir === "flat" ? "–" : `${dir === "up" ? "▲" : "▼"} ${Math.round(Math.abs(pct))}%`}
    </span>
  );
}

function MiniBars({
  entries,
}: {
  entries: { label: string; count: number }[];
}) {
  const max = Math.max(1, ...entries.map((e) => e.count));
  return (
    <div className="space-y-1">
      {entries.map((e) => (
        <div key={e.label} className="flex items-center gap-2 text-xs">
          <span className="w-16 shrink-0 text-gray-500 dark:text-gray-400">{e.label}</span>
          <div className="h-2 flex-1 overflow-hidden rounded-full bg-gray-100 dark:bg-gray-800">
            <div
              className="h-full rounded-full bg-[color:var(--accent,#2563eb)]"
              style={{ width: `${(e.count / max) * 100}%` }}
            />
          </div>
          <span className="w-6 shrink-0 text-right text-gray-500 dark:text-gray-400">{e.count}</span>
        </div>
      ))}
    </div>
  );
}

export interface ItemStatsDoseInput {
  displayUnit: string;
  total: number;
  avgPerLoggedDay: number;
  avgPerCalendarDay: number;
  peakDay: string | null;
  peakAmount: number;
  prevTotal: number | null;
  excludedNote: string | null;
}

interface Props {
  stats: OccurrenceStats;
  rangeDays: number;
  itemLabel: string;
  /** Medication items only — the Dose amounts card. */
  dose?: ItemStatsDoseInput;
  /** symptom/headache items only — the Episodes card. */
  episodes?: SymptomStatRow | null;
}

export function ItemStatsView({ stats, rangeDays, itemLabel, dose, episodes }: Props) {
  if (stats.totalOccurrences === 0) {
    return (
      <p className="mt-6 text-center text-sm text-gray-400 dark:text-gray-500">
        No entries in the last {rangeDays} days.
      </p>
    );
  }

  const verb = dose ? "Taken" : "Logged";
  const s = stats;

  const lastLogged =
    s.daysSinceLast === null ? "never" : s.daysSinceLast === 0 ? "today" : `${s.daysSinceLast} days ago`;

  const intervalLine =
    s.avgIntervalDays === null
      ? "—"
      : s.avgIntervalDays < 1
        ? `${s.avgPerLoggedDay}× per day on days logged`
        : `About once every ${s.avgIntervalDays} days`;

  const showTrend = s.prev !== null || (dose?.prevTotal ?? null) !== null;

  return (
    <div className="grid gap-3 sm:grid-cols-2" aria-label={`Statistics for ${itemLabel}`}>
      <Card title="Frequency">
        <Stat label={verb} value={`${s.totalOccurrences}×`} />
        <Stat label="Days logged" value={`${s.daysLogged} of ${s.windowDays} (${s.pctDaysLogged}%)`} />
        <Stat label="Frequency" value={intervalLine} />
        <Stat label="Last logged" value={lastLogged} />
        <Stat label="Longest gap" value={`${s.longestZeroRunDays} days`} />
        <Stat
          label="Busiest day"
          value={s.peakDay ? `${formatDate(s.peakDay)} (${s.maxInOneDay}×)` : "—"}
        />
      </Card>

      <Card title="Streaks" hint="A streak is 2+ days in a row.">
        <Stat label="Streaks" value={`${s.streaks.count}`} />
        <Stat label="Longest" value={`${s.streaks.longest} days`} />
        <Stat label="Average" value={`${s.streaks.avgLength} days`} />
        <Stat label="Current" value={s.streaks.currentLength === 0 ? "None" : `${s.streaks.currentLength} days`} />
        <Stat
          label="Typical gap between streaks"
          value={s.streaks.avgGapDays === null ? "—" : `${s.streaks.avgGapDays} days`}
        />
        <Stat label="Isolated single days" value={`${s.streaks.singleDayCount}`} />
      </Card>

      {dose && (
        <Card title="Dose amounts">
          <Stat label="Total" value={`${dose.total} ${dose.displayUnit}`} />
          <Stat label="Average on days taken" value={`${dose.avgPerLoggedDay} ${dose.displayUnit}`} />
          <Stat label="Average per day" value={`${dose.avgPerCalendarDay} ${dose.displayUnit}`} />
          <Stat
            label="Highest day"
            value={dose.peakDay ? `${formatDate(dose.peakDay)} (${dose.peakAmount} ${dose.displayUnit})` : "—"}
          />
          {dose.excludedNote && (
            <p className="pt-1 text-[11px] text-gray-400 dark:text-gray-500">{dose.excludedNote}</p>
          )}
        </Card>
      )}

      <Card title="Timing">
        <MiniBars entries={TIME_BUCKETS.map((b) => ({ label: BUCKET_LABEL[b], count: s.timeOfDay[b] }))} />
        <Stat label="Most common time" value={s.mostCommonHour === null ? "—" : formatHour(s.mostCommonHour)} />
        <div className="pt-1">
          <MiniBars entries={s.dayOfWeek.map((count, i) => ({ label: DOW_LABEL[i], count }))} />
        </div>
      </Card>

      {showTrend && (
        <Card title="Trend">
          {s.prev !== null && (
            <>
              <Stat
                label="Occurrences"
                value={`${s.totalOccurrences} vs ${s.prev.totalOccurrences} previous ${s.windowDays}d`}
              />
              <div className="flex items-center justify-end">
                <Delta current={s.totalOccurrences} prev={s.prev.totalOccurrences} />
              </div>
              <Stat label="Days logged" value={`${s.daysLogged} vs ${s.prev.daysLogged}`} />
            </>
          )}
          {dose?.prevTotal != null && (
            <Stat label={`Total ${dose.displayUnit}`} value={`${dose.total} vs ${dose.prevTotal}`} />
          )}
        </Card>
      )}

      {episodes && (
        <Card title="Episodes">
          <Stat label="Episodes" value={`${episodes.sessionCount}`} />
          <Stat
            label="Resolved / ongoing"
            value={`${episodes.resolvedSessionCount} / ${episodes.ongoingSessionCount} (${episodes.resolvedPct}%)`}
          />
          <Stat
            label="Average time to resolve"
            value={episodes.avgResolutionMs != null ? formatResolutionMs(episodes.avgResolutionMs) : "—"}
          />
          <Stat
            label="Shortest / longest"
            value={
              episodes.minResolutionMs != null && episodes.maxResolutionMs != null
                ? `${formatResolutionMs(episodes.minResolutionMs)} / ${formatResolutionMs(episodes.maxResolutionMs)}`
                : "—"
            }
          />
          <Stat label="Severity" value={`avg ${episodes.avgSeverity}, peak ${episodes.maxSeverity}`} />
          <Stat label="Days with a check-in" value={`${episodes.distinctDaysLogged}`} />
        </Card>
      )}
    </div>
  );
}
